import { shellText } from '@vnetwork/datacentral-shell/i18n'
import { injectTourStyles } from '@vnetwork/datacentral-shell/tour-styles'
import React, {
    createContext,
    useContext,
    useRef,
    useCallback,
    useState,
} from 'react'
import { driver, Driver, DriveStep } from 'driver.js'
import 'driver.js/dist/driver.css'

interface TourStep {
    selector: string
    content: string
    title?: string
    position?: 'top' | 'right' | 'bottom' | 'left'
}

interface TourContextType {
    /** Start tour with steps from provider prop */
    start: () => void
    /** Start tour with custom steps */
    startTour: (steps: TourStep[], onComplete?: () => void) => void
    /** Exit current tour */
    exit: () => void
    exitTour: () => void
    /** Is tour currently active */
    isActive: boolean
    /** Current step index (0-based) */
    currentStep: number
    /** Set current step */
    setCurrentStep: (step: number) => void
}

const TourContext = createContext<TourContextType>({
    start: () => {},
    startTour: () => {},
    exit: () => {},
    exitTour: () => {},
    isActive: false,
    currentStep: 0,
    setCurrentStep: () => {},
})

export const useTour = () => {
    const context = useContext(TourContext)
    if (!context) {
        throw new Error('useTour must be used within a TourProvider')
    }
    return context
}

interface TourProviderProps {
    children: React.ReactNode
    /** Steps for backward compatibility with old API */
    steps?: TourStep[]
}

export const TourProvider: React.FC<TourProviderProps> = ({
    children,
    steps: providerSteps,
}) => {
    const driverRef = useRef<Driver | null>(null)
    const [isActive, setIsActive] = useState(false)
    const [currentStep, setCurrentStepState] = useState(0)

    const startTour = useCallback(
        (steps: TourStep[], onComplete?: () => void) => {
            // Exit any existing tour
            if (driverRef.current) {
                driverRef.current.destroy()
            }

            // Convert to driver.js format
            const driverSteps: DriveStep[] = steps.map((step) => ({
                element: step.selector,
                popover: {
                    title: step.title || '',
                    description: step.content,
                    side: step.position || 'bottom',
                    align: 'center' as const,
                },
            }))

            // Create driver instance with custom styling
            injectTourStyles()
            const driverInstance = driver({
                showProgress: true,
                showButtons: ['next', 'previous', 'close'],
                steps: driverSteps,
                nextBtnText: shellText('tour_popover.next'),
                prevBtnText: shellText('tour_popover.previous'),
                doneBtnText: shellText('tour_popover.finish'),
                progressText: '{{current}} / {{total}}',
                popoverClass: 'driverjs-theme',
                onHighlightStarted: (_element, step) => {
                    const index = driverSteps.findIndex((s) => s === step)
                    if (index >= 0) setCurrentStepState(index)
                },
                onDestroyStarted: () => {
                    setIsActive(false)
                    driverInstance.destroy()
                },
                onDestroyed: () => {
                    setIsActive(false)
                    setCurrentStepState(0)
                    onComplete?.()
                },
            })

            driverRef.current = driverInstance
            setIsActive(true)
            driverInstance.drive()
        },
        []
    )

    // Backward compatible start() - uses steps from provider prop
    const start = useCallback(() => {
        if (providerSteps && providerSteps.length > 0) {
            startTour(providerSteps)
        } else {
            console.warn(
                'TourProvider: No steps provided. Use steps prop or startTour(steps).'
            )
        }
    }, [providerSteps, startTour])

    const exit = useCallback(() => {
        if (driverRef.current) {
            driverRef.current.destroy()
            driverRef.current = null
        }
        setIsActive(false)
        setCurrentStepState(0)
    }, [])

    const setCurrentStep = useCallback((step: number) => {
        if (driverRef.current && step >= 0) {
            driverRef.current.moveTo(step)
            setCurrentStepState(step)
        }
    }, [])

    return (
        <TourContext.Provider
            value={{
                start,
                startTour,
                exit,
                exitTour: exit,
                isActive,
                currentStep,
                setCurrentStep,
            }}
        >
            {children}
            {/* Custom CSS for driver.js to match our design */}
            <style>{`
                .driver-popover {
                    background: var(--vn-bg-surface) !important;
                    color: var(--vn-fg-default) !important;
                    border: 1px solid var(--vn-border-default) !important;
                    border-radius: var(--vn-radius-lg) !important;
                    box-shadow: var(--vn-shadow-lg) !important;
                    max-width: 400px !important;
                    padding: var(--vn-space-4) !important;
                    font-family: var(--vn-font-sans) !important;
                }
                .driver-popover-title {
                    font-size: var(--vn-font-size-lg) !important;
                    font-weight: var(--vn-font-weight-semibold) !important;
                    color: var(--vn-fg-default) !important;
                    margin-bottom: var(--vn-space-2) !important;
                }
                .driver-popover-description {
                    font-size: var(--vn-font-size-md) !important;
                    color: var(--vn-fg-muted) !important;
                    line-height: 1.6 !important;
                }
                .driver-popover-progress-text {
                    font-size: var(--vn-font-size-sm) !important;
                    color: var(--vn-fg-subtle) !important;
                }
                .driver-popover-footer {
                    margin-top: var(--vn-space-4) !important;
                }
                .driver-popover-navigation-btns {
                    gap: var(--vn-space-2) !important;
                }
                .driver-popover-prev-btn,
                .driver-popover-next-btn {
                    padding: var(--vn-space-2) var(--vn-space-4) !important;
                    border-radius: var(--vn-radius-md) !important;
                    font-size: var(--vn-font-size-md) !important;
                    font-weight: var(--vn-font-weight-medium) !important;
                    transition: all 0.2s !important;
                    cursor: pointer !important;
                    text-shadow: none !important;
                    -webkit-font-smoothing: antialiased !important;
                    -moz-osx-font-smoothing: grayscale !important;
                    opacity: 1 !important;
                }
                .driver-popover-prev-btn {
                    background: var(--vn-bg-surface) !important;
                    color: var(--vn-fg-default) !important;
                    border: 1px solid var(--vn-border-default) !important;
                }
                .driver-popover-prev-btn:hover {
                    background: var(--vn-bg-hover) !important;
                }
                .driver-popover-next-btn {
                    background: var(--vn-primary-solid) !important;
                    color: var(--vn-fg-on-solid) !important;
                    border: none !important;
                    text-shadow: none !important;
                }
                .driver-popover-next-btn:hover {
                    background: var(--vn-primary-solid-hover) !important;
                }
                .driver-popover-close-btn {
                    color: var(--vn-fg-subtle) !important;
                    width: 28px !important;
                    height: 28px !important;
                }
                .driver-popover-close-btn:hover {
                    color: var(--vn-fg-default) !important;
                    background: var(--vn-bg-hover) !important;
                    border-radius: var(--vn-radius-sm) !important;
                }
                .driver-popover-arrow-side-left,
                .driver-popover-arrow-side-right,
                .driver-popover-arrow-side-top,
                .driver-popover-arrow-side-bottom {
                    border-color: var(--vn-bg-surface) !important;
                }
                .driver-overlay {
                    opacity: 1 !important;
                }
                .driver-active-element {
                    opacity: 1 !important;
                    filter: none !important;
                }
            `}</style>
        </TourContext.Provider>
    )
}
